import SearchIcon from "@mui/icons-material/Search";
import {
  InputAdornment,
  TextField,
  type SxProps,
  type Theme,
} from "@mui/material";
import { type ChangeEvent, useEffect, useState } from "react";

import { useDebouncedValue } from "hooks/useDebouncedValue";

interface SearchBarProps {
  /** Called with the debounced search value */
  onSearchChange: (_value: string) => void;
  /** Placeholder text */
  placeholder?: string;
  /** Debounce delay in ms (default: 300) */
  debounceMs?: number;
  /** Optional sx overrides */
  sx?: SxProps<Theme>;
}

/**
 * Detached search bar for use with ServerSidePaginatedDataGrid.
 *
 * Place anywhere in your layout. Wire `onSearchChange` to update `tableParams.search`
 * in the parent, and the DataGrid will re-query the API with the search value.
 *
 * @example
 * ```tsx
 * <SearchBar
 *   onSearchChange={(value) =>
 *     setTableParams((prev) => ({ ...prev, search: value, page: 0 }))
 *   }
 * />
 * ```
 */
export default function SearchBar({
  onSearchChange,
  placeholder = "Search...",
  debounceMs = 300,
  sx,
}: SearchBarProps) {
  const [inputValue, setInputValue] = useState("");
  const debouncedValue = useDebouncedValue(inputValue, debounceMs);

  useEffect(() => {
    onSearchChange(debouncedValue);
  }, [debouncedValue, onSearchChange]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  return (
    <TextField
      value={inputValue}
      onChange={handleChange}
      placeholder={placeholder}
      size="small"
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        },
      }}
      sx={[
        {
          "& .MuiOutlinedInput-root": {
            borderRadius: "16px",
          },
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    />
  );
}
